import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert, 
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Fade,
  IconButton
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Assignment as SurveyIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Nature as CropIcon,
  Science as SampleIcon,
  Info as InfoIcon,
  DateRange as DateIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';
import { SeedSurvey } from '../types';

// Estilos para impresión - optimizado para una sola página
const printStyles = `
  @media print {
    /* Configuración de página */
    @page {
                  {/* Additional Information */}
            <Box>
              <InfoCard title={t('additionalInformation')} icon={<InfoIcon />}>
                {survey.notes && (
                  <DetailItem label={t('notes')} value={survey.notes} multiline />
                )}
                <DetailItem 
                  label={t('createdBy')} 
                  value={survey.created_by_username || ''} 
                />
                <DetailItem 
                  label={t('createdAt')} 
                  value={new Date(survey.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')} 
                />
                <DetailItem 
                  label={t('updatedAt')} 
                  value={new Date(survey.updated_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')} 
                />
                
                {/* Survey Status Information */}
                <DetailItem 
                  label={t('herbariumSample')} 
                  value={survey.is_herbarium_sample ? t('yes') : t('no')} 
                  chip 
                  chipColor={survey.is_herbarium_sample ? 'success' : 'default'}
                />
                <DetailItem 
                  label={t('hasPhotos')} 
                  value={survey.has_photos ? t('yes') : t('no')} 
                  chip 
                  chipColor={survey.has_photos ? 'success' : 'default'}
                />
              </InfoCard>
            </Box>* تغيير الاتجاه إلى عمودي */
      margin: 5mm; /* هوامش صغيرة */
    }
    
    /* Ocultar elementos que no son parte de la encuesta */
    body * {
      visibility: hidden;
    }
    
    /* Mostrar solo el contenido de la encuesta */
    .print-container, .print-container * {
      visibility: visible;
      overflow: visible;
    }
    
    /* Ajustes generales para la impresión */
    .print-container {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 0 !important;
      margin: 0 !important;
      background-color: white !important;
      font-size: 8pt !important; /* Reducir tamaño de fuente general */
    }
    
    /* Estilos para elementos específicos */
    .MuiPaper-root {
      background-color: white !important;
      color: black !important;
      box-shadow: none !important;
      border: none !important;
      padding: 5px !important;
    }
    
    /* Ocultar elementos no necesarios para la impresión */
    .no-print {
      display: none !important;
    }
    
    /* Mostrar elementos solo para impresión */
    .print-only {
      display: block !important;
    }
    
    /* Ajustes para tarjetas y chips */
    .MuiCard-root {
      box-shadow: none !important;
      border: 0.5px solid #ddd !important;
      margin-bottom: 5px !important;
      padding: 3px !important;
    }
    
    .MuiCardContent-root {
      padding: 5px !important;
    }
    
    .MuiChip-root {
      border: 0.5px solid #ddd !important;
      height: 20px !important;
      font-size: 7pt !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    
    /* Eliminar saltos de página */
    .print-break {
      page-break-before: auto;
    }
    
    /* Ajustes para imágenes - optimizadas para orientación vertical */
    img {
      max-width: 70px !important;
      max-height: 70px !important;
    }
    
    /* Ajustes para texto */
    h1, h2, h3, h4, h5, h6 {
      color: black !important;
      margin: 3px 0 !important;
      font-size: 10pt !important;
    }
    
    /* Tamaños de texto optimizados para orientación vertical */
    .MuiTypography-h4 {
      font-size: 11pt !important;
    }
    
    .MuiTypography-h6 {
      font-size: 9pt !important;
    }
    
    .MuiTypography-body1 {
      font-size: 7pt !important;
      margin: 1px 0 !important;
    }
    
    .MuiTypography-body2 {
      font-size: 6pt !important;
    }
    
    /* Eliminar gradientes y fondos complejos */
    [style*="background"] {
      background: white !important;
      color: black !important;
    }
    
    /* Optimizar layout */
    .MuiBox-root {
      padding: 2px !important;
      margin: 2px !important;
    }
    
    /* Hacer que los contenedores flex se ajusten mejor */
    .MuiBox-root[style*="display: flex"] {
      flex-wrap: wrap !important;
      gap: 3px !important;
    }
    
    /* Ajustar tamaño de avatares */
    .MuiAvatar-root {
      width: 20px !important;
      height: 20px !important;
    }
    
    /* Ajustar espaciado entre elementos */
    .MuiBox-root > * {
      margin-bottom: 3px !important;
    }
    
    /* Optimizar el diseño de cuadrícula para una sola página en orientación vertical */
    .print-container .MuiBox-root[style*="display: flex"] > .MuiBox-root {
      flex: 0 0 45% !important; /* Aumentar el ancho para mejor ajuste en modo vertical */
      min-width: auto !important;
    }
  }
`;

interface ParticipantDetails {
  id: number;
  name: string;
  phone: string;
  civilid?: string;
  department?: number;
  department_name?: string;
  department_name_ar?: string;
  department_name_en?: string;
  departments?: number[];
  departments_data?: { id: number; name_ar: string; name_en: string }[];
  // New structure for participants
  participant?: {
    id: number;
    name: string;
    phone: string;
    civilid: string;
    department?: number;
    department_name_ar?: string;
    department_name_en?: string;
    primary_department?: number;
    departments: { id: number; name_ar: string; name_en: string }[];
  };
}

const SurveyDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [survey, setSurvey] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  
  // Función para manejar la impresión
  const handlePrint = () => {
    setIsPrinting(true);
    
    // Preparar la página para impresión
    const originalTitle = document.title;
    document.title = `${t('surveyDetails')} - ${survey?.survey_number || ''}`;  
    
    // Dar tiempo para que los estilos se apliquen
    setTimeout(() => {
      window.print();
      
      // Restaurar después de imprimir
      setTimeout(() => {
        document.title = originalTitle;
        setIsPrinting(false);
      }, 300);
    }, 300);
  };

  // Función para extraer datos de ubicación
  const extractLocationData = (data: any) => {
    // Extraer datos de ubicación de diferentes estructuras posibles
    let locationData = {
      village: '',
      state: '',
      governorate: ''
    };

    try {
      // Intentar obtener datos de diferentes estructuras
      if (data) {
        // Para pueblo/aldea (village)
        if (data.village && typeof data.village === 'object') {
          locationData.village = i18n.language === 'ar' ? data.village.name_ar : data.village.name_en;
        }

        // Para estado/wilaya (state) - usar state_name_ar/en del objeto village
        if (data.village && data.village.state_name_ar && data.village.state_name_en) {
          locationData.state = i18n.language === 'ar' ? data.village.state_name_ar : data.village.state_name_en;
        }

        // Para gobernación/provincia (governorate) - usar governorate_name_ar/en del objeto village
        if (data.village && data.village.governorate_name_ar && data.village.governorate_name_en) {
          locationData.governorate = i18n.language === 'ar' ? data.village.governorate_name_ar : data.village.governorate_name_en;
        }
      }
    } catch (error) {
      console.error('Error extracting location data:', error);
    }

    console.log('Extracted location data:', locationData);
    return locationData;
  };

  // Estado para datos de ubicación
  const [locationData, setLocationData] = useState({ village: '', state: '', governorate: '' });

  useEffect(() => {
    const fetchSurvey = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.getSurvey(parseInt(id));
        
        // Registro detallado para depuración
        console.log('Survey data structure:', JSON.stringify(response, null, 2));
        console.log('Village data:', response.village);
        console.log('Village details:', response.village_details);
        // Usar acceso seguro para evitar errores de TypeScript
        console.log('State data:', (response as any).state);
        console.log('Governorate data:', (response as any).governorate);
        
        // Forzar datos de ubicación para pruebas
        const testLocationData = {
          village: 'Mawalh',
          state: 'Wilayat Al Seeb',
          governorate: 'Muscat'
        };
        
        // Establecer datos de encuesta
        setSurvey(response);
        
        // Extraer y establecer datos de ubicación
        const extractedLocationData = extractLocationData(response);
        console.log('Extracted location data:', extractedLocationData);
        
        // Usar datos de prueba si los extraídos están vacíos
        if (!extractedLocationData.state && !extractedLocationData.governorate) {
          setLocationData(testLocationData);
        } else {
          setLocationData(extractedLocationData);
        }
      } catch (err) {
        setError(t('loadError'));
        console.error('Failed to fetch survey:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [id, t, i18n.language]);

  // Insertar los estilos de impresión en el DOM
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = printStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  if (error || !survey) {
    return (
      <Box sx={{ p: 0 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error || t('surveyNotFound')}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/surveys')}
          sx={{ mt: 2 }}
        >
          {t('backToSurveys')}
        </Button>
      </Box>
    );
  }

  const InfoCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #f1f5f9',
      height: '100%',
      '@media print': {
        boxShadow: 'none',
        border: '1px solid #ddd'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{ 
            bgcolor: '#667eea', 
            width: 40, 
            height: 40,
            '& svg': { fontSize: '1.2rem' },
            '@media print': {
              bgcolor: '#ddd'
            }
          }}>
            {icon}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  const DetailItem: React.FC<{ 
    label: string; 
    value: string | number | null | undefined; 
    chip?: boolean; 
    icon?: React.ReactNode; 
    multiline?: boolean; 
    chipColor?: 'success' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'warning' 
  }> = ({ label, value, chip = false, icon, multiline = false, chipColor = 'default' }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 0.5 }}>
        {label}
      </Typography>
      {chip ? (
        <Chip 
          label={value || 'N/A'}
          size="small"
          sx={{
            fontWeight: 500,
            '@media print': {
              border: '1px solid #ddd',
              color: 'black'
            }
          }}
          color={chipColor}
        />
      ) : (
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 500, 
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
            color: '#374151'
          }}
        >
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Fade in={true}>
      <Box sx={{ position: 'relative', overflow: 'hidden' }} className="print-container">
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            mb: 4,
            '@media print': {
              background: 'white',
              color: 'black',
              p: 2,
              mb: 2
            }
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            '@media print': {
              display: 'none'
            }
          }} />
          
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 4,
            pb: 3,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            '@media print': {
              borderBottom: '1px solid #ddd'
            }
          }}>
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/surveys')}
                sx={{
                  color: 'white',
                  mb: 2,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)'
                  },
                  '@media print': {
                    display: 'none'
                  }
                }}
                className="no-print"
              >
                {t('backToSurveys')}
              </Button>
              <Typography variant="h4" component="h1" sx={{ 
                fontWeight: 700, 
                mb: 1,
                '@media print': {
                  color: '#000'
                }
              }}>
                📋 {t('surveyDetails')}
              </Typography>
              <Typography variant="h6" sx={{ 
                opacity: 0.9, 
                fontWeight: 400,
                '@media print': {
                  color: '#000',
                  opacity: 1
                }
              }}>
                {t('surveyNumber')}: {survey.survey_number}
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              gap: 1,
              '@media print': {
                display: 'none'
              }
            }} className="no-print">
              <Button
                startIcon={<PrintIcon />}
                variant="contained"
                color="secondary"
                onClick={handlePrint}
              >
                {t('print')}
              </Button>
              <IconButton
                onClick={() => navigate(`/survey/edit/${survey.id}`)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Participants Section */}
            <Box sx={{ flex: '1 1 100%', minWidth: '300px' }}>
              <InfoCard title={t('participants')} icon={<GroupIcon />}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {survey.participants && survey.participants.map((participantData: ParticipantDetails, index: number) => {
                    // Handle both old and new participant data structures
                    const participant = participantData.participant || participantData;
                    
                    // Get department names from the correct location with safe access
                    const departmentNameAr = participant.department_name_ar || participantData.department_name_ar;
                    const departmentNameEn = participant.department_name_en || participantData.department_name_en;
                    
                    // Determine which department name to display
                    const departmentDisplay = i18n.language === 'ar' ? 
                      (departmentNameAr || departmentNameEn) : 
                      (departmentNameEn || departmentNameAr);
                    
                    console.log('Participant:', participant.name, 'Department AR:', departmentNameAr, 'Department EN:', departmentNameEn);
                    
                    return (
                    <Box key={index} sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid',
                      borderColor: 'rgba(16, 185, 129, 0.2)',
                      '@media print': {
                        bgcolor: 'white',
                        border: '1px solid #ddd'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: '#10b981',
                          '@media print': {
                            bgcolor: '#ddd'
                          }
                        }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {participant.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: '0.9rem', opacity: 0.7 }} />
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              {participant.phone}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      {/* Civil ID Display */}
                      {participant.civilid && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>
                            {t('civilId')}: {participant.civilid}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Department Display */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {departmentDisplay ? (
                          <Chip 
                            label={departmentDisplay}
                            size="small" 
                            color="success"
                            sx={{ m: 0.5 }}
                          />
                        ) : (
                          <Chip 
                            label={t('noDepartment')}
                            size="small" 
                            color="default"
                            sx={{ m: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                    );
                  })}
                </Box>
              </InfoCard>
            </Box>

            {/* Basic Information */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {/* Survey Information */}
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('surveyInformation')} icon={<SurveyIcon />}>
                  <DetailItem label={t('surveyNumber')} value={survey.survey_number} />
                  <DetailItem label={t('dateCollected')} value={survey.date_collected} />
                  <DetailItem 
                    label={t('farmerName')} 
                    value={survey.farmer?.name || survey.farmer_name} 
                  />
                  <DetailItem 
                    label={t('farmerPhone')} 
                    value={survey.farmer?.phone || survey.farmer_phone} 
                  />
                  {(survey.farmer?.civilid || survey.farmer_civilid) && (
                    <DetailItem 
                      label={t('civilId')} 
                      value={survey.farmer?.civilid || survey.farmer_civilid} 
                    />
                  )}
                </InfoCard>
              </Box>

              {/* Location Information */}
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('locationInformation')} icon={<LocationIcon />}>
                  <DetailItem 
                    label={t('village')} 
                    value={survey.village?.name_ar && survey.village?.name_en ? 
                      (i18n.language === 'ar' ? survey.village.name_ar : survey.village.name_en) :
                      (locationData.village || '')
                    } 
                  />
                  <DetailItem 
                    label={t('state')} 
                    value={survey.village?.state_details?.name_ar && survey.village?.state_details?.name_en ?
                      (i18n.language === 'ar' ? survey.village.state_details.name_ar : survey.village.state_details.name_en) :
                      (locationData.state || '')
                    } 
                  />
                  <DetailItem 
                    label={t('governorate')} 
                    value={survey.village?.state_details?.governorate_details?.name_ar && survey.village?.state_details?.governorate_details?.name_en ?
                      (i18n.language === 'ar' ? survey.village.state_details.governorate_details.name_ar : survey.village.state_details.governorate_details.name_en) :
                      (locationData.governorate || '')
                    } 
                  />
                  {(survey.auto_latitude || survey.latitude) && (survey.auto_longitude || survey.longitude) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        color: '#374151', 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        {t('coordinates')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          {`${Number(survey.auto_latitude || survey.latitude).toFixed(6)}, ${Number(survey.auto_longitude || survey.longitude).toFixed(6)}`}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocationIcon />}
                          onClick={() => {
                            const lat = Number(survey.auto_latitude || survey.latitude);
                            const lng = Number(survey.auto_longitude || survey.longitude);
                            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                          }}
                          sx={{ 
                            minWidth: 'auto',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem'
                          }}
                        >
                          {t('viewOnMap')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {(survey.elevation || survey.altitude) && (
                    <DetailItem 
                      label={t('elevation')} 
                      value={`${survey.elevation || survey.altitude} ${t('meters')}`} 
                    />
                  )}
                  {survey.location_description && (
                    <DetailItem 
                      label={t('locationDescription')} 
                      value={survey.location_description} 
                      multiline 
                    />
                  )}
                </InfoCard>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {/* Crop Information */}
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('cropInformation')} icon={<CropIcon />}>
                  <DetailItem 
                    label={t('crop')} 
                    value={i18n.language === 'ar' ? (survey.crop?.name_ar || '') : (survey.crop?.name_en || '')} 
                  />
                  {survey.variety_number && (
                    <DetailItem 
                      label={t('varietyNumber')} 
                      value={survey.variety_number} 
                    />
                  )}
                  {survey.crop_age && (
                    <DetailItem 
                      label={t('cropAge')} 
                      value={`${survey.crop_age} ${t('years')}`} 
                    />
                  )}
                  {/* Usage - Handle multiple selection */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      color: '#374151', 
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {t('usage')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Array.isArray(survey.usage) ? (
                        survey.usage.map((usage: any, index: number) => (
                          <Chip
                            key={index}
                            label={i18n.language === 'ar' ? usage.name_ar : usage.name_en}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : survey.usage ? (
                        <Chip
                          label={i18n.language === 'ar' ? survey.usage.name_ar : survey.usage.name_en}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
                          {t('unknown')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {survey.local_name && (
                    <DetailItem label={t('localName')} value={survey.local_name} />
                  )}
                </InfoCard>
              </Box>

              {/* Sample Information */}
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('sampleInformation')} icon={<SampleIcon />}>
                  <DetailItem 
                    label={t('sampleType')} 
                    value={
                      survey.sample_type 
                        ? (i18n.language === 'ar' ? survey.sample_type.name_ar : survey.sample_type.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('geneticStatus')} 
                    value={
                      survey.genetic_status 
                        ? (i18n.language === 'ar' ? survey.genetic_status.name_ar : survey.genetic_status.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('herbariumSample')} 
                    value={survey.is_herbarium_sample ? t('yes') : t('no')} 
                    chip 
                    chipColor={survey.is_herbarium_sample ? 'success' : 'default'}
                  />
                  <DetailItem 
                    label={t('seedOriginLocal')} 
                    value={survey.is_local_seed ? t('yes') : t('no')} 
                    chip 
                    chipColor={survey.is_local_seed ? 'success' : 'default'}
                  />
                </InfoCard>
              </Box>
            </Box>

            {/* Topography and Location Status */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('topographyInformation')} icon={<InfoIcon />}>
                  <DetailItem 
                    label={t('topography')} 
                    value={
                      survey.topography 
                        ? (i18n.language === 'ar' ? survey.topography.name_ar : survey.topography.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('locationStatus')} 
                    value={
                      survey.location_status 
                        ? (i18n.language === 'ar' ? survey.location_status.name_ar : survey.location_status.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('collectionSource')} 
                    value={
                      survey.collection_source 
                        ? (i18n.language === 'ar' ? survey.collection_source.name_ar : survey.collection_source.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('material')} 
                    value={
                      survey.material 
                        ? (i18n.language === 'ar' ? survey.material.name_ar : survey.material.name_en)
                        : t('unknown')
                    } 
                  />
                </InfoCard>
              </Box>
              
              {/* Soil Information */}
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <InfoCard title={t('soilInformation')} icon={<InfoIcon />}>
                  <DetailItem 
                    label={t('soilTexture')} 
                    value={
                      survey.soil_texture 
                        ? (i18n.language === 'ar' ? survey.soil_texture.name_ar : survey.soil_texture.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('soilColor')} 
                    value={
                      survey.soil_color 
                        ? (i18n.language === 'ar' ? survey.soil_color.name_ar : survey.soil_color.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('drainage')} 
                    value={
                      survey.drainage 
                        ? (i18n.language === 'ar' ? survey.drainage.name_ar : survey.drainage.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('soilPH')} 
                    value={
                      survey.soil_ph 
                        ? (i18n.language === 'ar' ? survey.soil_ph.name_ar : survey.soil_ph.name_en)
                        : t('unknown')
                    } 
                  />
                  <DetailItem 
                    label={t('waterPH')} 
                    value={
                      survey.water_ph 
                        ? (i18n.language === 'ar' ? survey.water_ph.name_ar : survey.water_ph.name_en)
                        : t('unknown')
                    } 
                  />
                </InfoCard>
              </Box>
            </Box>

            {/* Diseases and Pests */}
            {survey.diseases_pests && survey.diseases_pests.length > 0 && (
              <Box>
                <InfoCard title={t('diseasesAndPests')} icon={<InfoIcon />}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {survey.diseases_pests.map((item: any, index: number) => (
                      <Chip 
                        key={index}
                        label={i18n.language === 'ar' ? item.name_ar : item.name_en}
                        color={item.type === 'disease' ? 'error' : 'warning'}
                        size="medium"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </InfoCard>
              </Box>
            )}

            {/* Photos */}
            {survey.photos && survey.photos.length > 0 && (
              <Box className="print-break">
                <InfoCard title={t('photos')} icon={<InfoIcon />}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {survey.photos.map((photo: any, index: number) => (
                      <Box 
                        key={index}
                        sx={{ 
                          width: 200, 
                          height: 200, 
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: 1,
                          position: 'relative',
                          '@media print': {
                            boxShadow: 'none',
                            border: '1px solid #ddd'
                          }
                        }}
                      >
                        <img 
                          src={photo.photo} 
                          alt={`Survey photo ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }} 
                        />
                        {photo.description && (
                          <Box sx={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            p: 1,
                            fontSize: '0.8rem',
                            '@media print': {
                              bgcolor: 'white',
                              color: 'black',
                              border: '1px solid #ddd'
                            }
                          }}>
                            {photo.description}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </InfoCard>
              </Box>
            )}

            {/* Additional Information - visible only on screen */}
            <Box className="no-print">
              <InfoCard title={t('additionalInformation')} icon={<InfoIcon />}>
                {survey.notes && (
                  <DetailItem label={t('notes')} value={survey.notes} multiline />
                )}
                <DetailItem 
                  label={t('createdBy')} 
                  value={survey.created_by_username || ''} 
                />
                <DetailItem 
                  label={t('createdAt')} 
                  value={new Date(survey.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')} 
                />
                <DetailItem 
                  label={t('updatedAt')} 
                  value={new Date(survey.updated_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')} 
                />
              </InfoCard>
            </Box>
            
            {/* Notes section for print only if notes exist */}
            {survey.notes && (
              <Box className="print-only">
                <InfoCard title={t('notes')} icon={<InfoIcon />}>
                  <DetailItem label="" value={survey.notes} multiline />
                </InfoCard>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default SurveyDetailsPage;
